import { WinBoostBadge } from "../components/WinBoostBadge";
import { bookWait } from "../lib/bookWait";
import { tx } from "../lib/miniI18n";
import { ResultDigits } from "../components/ResultDigits";
import { QueenReveal } from "../components/QueenReveal";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { RateNoticeBanner } from "../components/RateNoticeBanner";
import { ArrowLeft, Clock, Trophy, Dice5, Flame, Star, Eraser, Lock } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { WinPatternBoard } from "../components/WinPatternBoard";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { SheetEntry } from "../components/SheetEntry";
import Countdown from "../components/Countdown";
import LuckyLive from "../components/LuckyLive";
import { RoyalStage } from "../components/RoyalStage";
import { slotLabel } from "../lib/slotVibe";
const to12h = (tm) => { if (!tm) return ""; const [h, m] = tm.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
import { useI18n } from "../i18n";
import { StoryButton } from "../components/StoryButton";
import LuckyHotNumbers from "../components/LuckyHotNumbers";
import RepeatLastRound from "../components/RepeatLastRound";
import { BookedStrip } from "../components/BookedStrip";
import { StreakChip } from "../components/StreakChip";
import { RateCardSheet } from "../components/RateCardSheet";
import { Skeleton } from "../components/Common";
import { fmt, dateTimeLabel, rateLine } from "../lib/format";
import { boardOf, orderedGroups, POS_COLORS, boxPermutations, supportsBox } from "../lib/board";


// Pick n distinct digits weighted by how often they appeared in last week's results
function suggestHotBox(results, n) {
  const since = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  let pool = (results || []).filter((r) => (r.draw_date || "") >= since);
  if (pool.length < 3) pool = results || [];
  const freq = Array(10).fill(0);
  pool.forEach((r) => String(r.winning_number || "").split("").forEach((d) => { freq[+d] += 1; }));
  const picked = [];
  const avail = Array.from({ length: 10 }, (_, d) => d);
  while (picked.length < n && avail.length) {
    const weights = avail.map((d) => freq[d] + 0.5);
    let roll = Math.random() * weights.reduce((a, b) => a + b, 0);
    let idx = 0;
    for (; idx < avail.length - 1; idx++) { roll -= weights[idx]; if (roll <= 0) break; }
    picked.push(avail.splice(idx, 1)[0]);
  }
  return picked;
}

const ResultBalls = ({ number, size }) => <ResultDigits number={number} size={size} />;

const WAIT_SECS = () => bookWait();  // seconds to type a qty / flip BOX after the number is complete (admin setting, default 5)
const EMPTY = (n) => ({ digits: Array(n).fill(null), qty: 1, mode: "direct" });

/* Quantity: direct numeric typing (mobile keypad) with +/- helpers */
/* Qty box: typing a quantity auto-adds the line (after a short pause, on Enter, or when leaving the box) — ADD button still works too. */
function QtyInput({ qty, setQty, testId, onCommit, onFocus }) {
  const timer = React.useRef(null);
  const touched = React.useRef(false);
  const commit = (q) => { if (onCommit && touched.current && q > 0) { touched.current = false; onCommit(q); } };
  return (
    <label className="flex items-center gap-1.5" data-testid={`${testId}-qty-box`}>
      <span className="text-[10px] font-bold text-slate-500">{tx("QTY")}</span>
      <input type="text" inputMode="numeric" pattern="[0-9]*" value={qty === 0 ? "" : qty} onFocus={(e) => { onFocus?.(); e.target.select(); }} placeholder="1" enterKeyHint="done"
        onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 4); const q = v === "" ? 0 : Math.max(0, parseInt(v, 10)); setQty(q); touched.current = true; clearTimeout(timer.current); if (q > 0) timer.current = setTimeout(() => commit(q), 900); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); clearTimeout(timer.current); commit(qty); e.target.blur(); } }}
        onBlur={() => { clearTimeout(timer.current); if (!qty) setQty(1); else commit(qty); }}
        className="w-16 h-10 text-center font-bold text-base text-slate-900 bg-white rounded-xl border-2 border-slate-300 outline-none focus:border-blue-500" aria-label="Quantity" data-testid={`${testId}-qty`} />
    </label>
  );
}

function SlatCard({ slat, game, onAdd, rateMode, results, draft, setDraft , cartItems = [], onRemove }) {
  const board = boardOf(slat);
  const positions = board.positions;
  const n = positions.length;
  const isParty = rateMode === "party";
  const rate = isParty ? (slat.party_rate || slat.rate) : slat.rate;
  const win = isParty ? (slat.party_win_amount || slat.win_amount) : slat.win_amount;
  const d = draft && draft.digits?.length === n ? draft : EMPTY(n);
  const { digits, qty, mode } = d;
  const patch = (x) => setDraft({ ...d, ...x });
  const [active, setActive] = useState(null);
  const [sheet, setSheet] = useState(false);
  const canBox = supportsBox(slat);
  const isBox = canBox && mode === "box";

  const filled = digits.every((x) => x !== null);
  const perms = filled && isBox ? boxPermutations(digits.join("")).length : 1;
  const lineTotal = rate * (qty || 0) * perms;
  const hasEntry = digits.some((x) => x !== null) || qty !== 1;

  const dRef = useRef(d); dRef.current = d;
  const timerRef = useRef(null);
  const [countdown, setCountdown] = useState(0);
  const cancelPending = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } setCountdown(0); };
  useEffect(() => () => cancelPending(), []);  // eslint-disable-line
  // Every board: number complete → 5-second window to type a qty or flip DIRECT ⇄ BOX; then it books (1 SET unless changed).
  const scheduleAdd = (next) => {
    cancelPending();
    let left = WAIT_SECS(); setCountdown(left);
    timerRef.current = setInterval(() => {
      left -= 1;
      if (left > 0) { setCountdown(left); return; }
      cancelPending();
      const cur = dRef.current;
      if (cur.digits.every((x) => x !== null)) add(true, cur.qty || 1, cur.digits, canBox && cur.mode === "box");
    }, 1000);
  };

  const setDigit = (v) => {
    if (active === null) return;
    const next = [...digits];
    next[active] = v;
    patch({ digits: next });
    const nextEmpty = next.findIndex((x) => x === null);
    setActive(nextEmpty === -1 ? null : nextEmpty);
    if (nextEmpty === -1) scheduleAdd(next);
  };

  const random = () => { patch({ digits: Array.from({ length: n }, () => Math.floor(Math.random() * 10)) }); setActive(null); };
  const suggestBox = () => { const hot = suggestHotBox(results, n); patch({ digits: hot, mode: "box" }); setActive(null); toast.success("Hot digits from last week's results"); api.post("/guess-log", { game_id: game.id, board: "BOX", number: (hot || []).join(""), source: "hotbox" }).catch(() => {}); };
  const reset = () => { cancelPending(); setDraft(null); setActive(null); };

  const add = (auto = false, q = qty, ds = digits, box = isBox) => {
    const ok = ds.every((x) => x !== null);
    if (!ok) { if (auto === true) return; toast.error(`${slat.name}: pick all ${n} number(s)`); setActive(ds.findIndex((x) => x === null)); return; }
    if (!q) { if (auto === true) return; toast.error("Enter quantity"); return; }
    const qty = q;
    const number = ds.join("");
    const p = box ? boxPermutations(number).length : 1;
    onAdd({ game_id: game.id, game_name: game.name, slat_id: slat.id, slat_name: slat.name, number, quantity: qty, rate, rate_type: rateMode, mode: box ? "box" : "direct", perms: p });
    toast.success(box ? `${slat.name} · ${number} BOX (${p} tickets) added` : `${slat.name} · ${number} × ${qty} added`);
    reset();
  };
  const addFromSheet = (number, q) => {
    const p = isBox ? boxPermutations(number).length : 1;
    onAdd({ game_id: game.id, game_name: game.name, slat_id: slat.id, slat_name: slat.name, number, quantity: q, rate, rate_type: rateMode, mode: isBox ? "box" : "direct", perms: p });
    return true;
  };

  const tagCls = board.tag === "ALL BOARD" ? "bg-red-100 text-red-700" : isBox ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600";
  const tagText = canBox ? (isBox ? "BOX" : "DIRECT") : board.tag;

  return (
    <div className="rl-card p-4" data-testid={`slat-card-${slat.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-slate-900 text-base">{slat.name}</span>
            {tagText && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${tagCls}`}>{tagText}</span>}
            <span className="text-[11px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md">Win {fmt(win)}</span>
          </div>
          <div className="text-sm font-semibold text-slate-500 mt-0.5">{fmt(rate)} · {isBox ? board.label.replace("Direct", "Box") : board.label}{isParty && <span className="ml-1 text-amber-600 font-bold">· PARTY</span>}</div>
          {(slat.cons_abc > 0 || slat.cons_bc > 0 || slat.cons_c > 0) && (
            <div className="flex gap-1 mt-1 flex-wrap" data-testid={`slat-consolation-${slat.id}`}>
              {slat.board_type === "4D" && slat.cons_abc > 0 && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">ABC {fmt(slat.cons_abc)}</span>}
              {slat.cons_bc > 0 && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">BC {fmt(slat.cons_bc)}</span>}
              {slat.cons_c > 0 && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">C {fmt(slat.cons_c)}</span>}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <button onClick={() => setSheet((v) => !v)} className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 ${sheet ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-800 hover:bg-amber-200"}`} data-testid={`slat-sheet-toggle-${slat.id}`}>📝 {sheet ? "Close sheet" : "10-row sheet"}</button>
          <button onClick={random} className="bg-slate-100 text-slate-600 hover:bg-slate-200 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1" data-testid={`slat-random-${slat.id}`}>
            <Dice5 className="w-3.5 h-3.5" />{tx("RANDOM")}
          </button>
          {canBox && (
            <button onClick={suggestBox} className="bg-amber-100 text-amber-800 hover:bg-amber-200 px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1" data-testid={`slat-suggest-box-${slat.id}`}>
              <Flame className="w-3.5 h-3.5" />{tx("SUGGEST BOX")}
            </button>
          )}
        </div>
      </div>

      {canBox && (
        <div className="flex gap-1 bg-slate-100 rounded-full p-1 mb-3 w-fit" data-testid={`slat-mode-toggle-${slat.id}`}>
          {[["direct", "DIRECT"], ["box", "BOX"]].map(([m, l]) => (
            <button key={m} onClick={() => patch({ mode: m })} data-testid={`slat-mode-${m}-${slat.id}`}
              className={`px-4 py-1.5 rounded-full text-[11px] font-bold transition-colors ${mode === m ? (m === "box" ? "bg-amber-500 text-white" : "bg-blue-600 text-white") : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      )}
      {/* Row 1: digits + Clear right beside them */}
      <div className="flex items-end gap-3 flex-wrap">
        {positions.map((p, i) => {
          const c = POS_COLORS[p] || POS_COLORS.A;
          return (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border-2 text-[11px] font-bold" style={{ borderColor: c.ring, color: c.text }}>{p}</span>
              <button onClick={() => setActive(i)} className="rl-digit w-[52px] h-[52px] text-xl"
                style={{ border: `3px solid ${c.ring}`, color: c.text, ...(active === i ? { boxShadow: `0 0 0 3px ${c.ring}44` } : {}) }} data-testid={`slat-digit-${slat.id}-${i}`}>
                {digits[i] === null ? "–" : digits[i]}
              </button>
            </div>
          );
        })}
        <button onClick={reset} disabled={!hasEntry} className="h-[52px] px-3 rounded-xl border-2 border-dashed border-red-300 text-red-600 text-xs font-bold flex items-center gap-1 disabled:opacity-30 active:scale-95" data-testid={`slat-clear-${slat.id}`}><Eraser className="w-4 h-4" />{tx("CLEAR")}</button>
      </div>
      {/* Keypad right under the digits (mobile: no scrolling down to find it) */}
      {active !== null && (
        <div className="mt-3 grid grid-cols-5 gap-2 rl-fade-up" data-testid={`slat-keypad-${slat.id}`}>
          {Array.from({ length: 10 }, (_, v) => (
            <button key={v} onClick={() => setDigit(v)} className="py-2.5 rounded-lg bg-slate-100 hover:bg-blue-50 font-mono font-bold text-slate-900 text-base active:scale-95 transition-transform" data-testid={`slat-key-${slat.id}-${v}`}>{v}</button>
          ))}
        </div>
      )}
      {/* Row 2: summary · quantity · ADD (well separated from Clear) */}
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap" data-testid={`slat-summary-${slat.id}`}>
        <div className="text-[11px] text-slate-600 leading-tight">
          <div><span className="text-slate-400">{tx("Board")}</span> <b>{slat.name}</b> · <span className="text-slate-400">{tx("Number")}</span> <b className="font-mono">{digits.map((x) => (x === null ? "–" : x)).join("")}</b></div>
          <div><span className="text-slate-400">{tx("Qty")}</span> <b>{qty || 0}</b> × {fmt(rate)}{perms > 1 ? ` × ${perms}` : ""} = <b className="text-blue-700" data-testid={`slat-amount-${slat.id}`}>{fmt(lineTotal)}</b></div>
        </div>
        <div className="flex items-center gap-2">
          <QtyInput qty={qty} setQty={(q) => patch({ qty: q })} testId={`slat-${slat.id}`} onFocus={cancelPending} onCommit={(q) => add(true, q)} />
          <button onMouseDown={(e) => e.preventDefault()} onTouchStart={(e) => { e.preventDefault(); add(false); }} onClick={() => add(false)} className="rl-gold-bg px-5 py-2.5 rounded-full text-xs font-bold active:scale-95 transition-transform" data-testid={`slat-add-${slat.id}`}>ADD</button>
        </div>
      </div>
      {countdown > 0 && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] font-bold text-amber-900 bg-amber-50 border border-amber-300 rounded-full pl-3 pr-1 py-1 w-fit" data-testid={`slat-countdown-${slat.id}`}>
          <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center font-black" data-testid={`slat-countdown-n-${slat.id}`}>{countdown}</span>
          {qty || 1} SET{canBox ? ` · ${mode === "box" ? "BOX" : "DIRECT"}` : ""} add ஆகுது… Qty{canBox ? " / BOX" : ""} மாற்றலாம்
          <button type="button" onClick={cancelPending} className="ml-1 w-6 h-6 rounded-full bg-white border border-amber-300 text-amber-800 flex items-center justify-center" title="Wait" data-testid={`slat-countdown-cancel-${slat.id}`}>✕</button>
        </div>
      )}
      {sheet && <SheetEntry digits={n} onAddRow={addFromSheet} label={`${slat.name}${isBox ? " BOX" : ""}`} testPrefix={`slat-sheet-${slat.id}`} />}
      {cartItems.filter((i) => i.slat_id === slat.id && i.game_id === game.id).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`slat-cart-chips-${slat.id}`}>
          {cartItems.filter((i) => i.slat_id === slat.id && i.game_id === game.id).map((i) => (
            <span key={i.key} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 pl-2.5 pr-1 py-0.5 text-[11px]" data-testid={`slat-cart-chip-${i.key}`}>
              <b className="font-mono">{i.number}</b>{i.mode === "box" ? " BOX" : ""} <span className="text-slate-500">×{i.quantity}</span>
              <button onClick={() => onRemove?.(i.key)} className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center" aria-label="Remove" data-testid={`slat-cart-remove-${i.key}`}>×</button>
            </span>
          ))}
        </div>
      )}

      {isBox && filled && (
        <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800" data-testid={`slat-box-info-${slat.id}`}>
          <b>{perms} tickets</b> · {boxPermutations(digits.join("")).join(", ")}
          <div className="mt-0.5 text-amber-700">{fmt(rate)} × {perms} × {qty} = <b>{fmt(lineTotal)}</b> · any order wins {fmt(win)}</div>
        </div>
      )}
    </div>
  );
}

/* ALL BOARD: one number + quantity → a ticket on every board of the group (A,B,C or AB,BC,AC) */
function AllBoardCard({ group, slats, game, onAdd, rateMode, draft, setDraft }) {
  const n = group === "single" ? 1 : 2;
  const d = draft && draft.digits?.length === n ? draft : EMPTY(n);
  const { digits, qty } = d;
  const patch = (x) => setDraft({ ...d, ...x });
  const [active, setActive] = useState(null);
  const isParty = rateMode === "party";
  const rateOf = (sl) => (isParty ? (sl.party_rate || sl.rate) : sl.rate);
  const filled = digits.every((x) => x !== null);
  const total = slats.reduce((t, sl) => t + rateOf(sl), 0) * (qty || 0);
  const hasEntry = digits.some((x) => x !== null) || qty !== 1;
  const dRef = useRef(d); dRef.current = d;
  const timerRef = useRef(null);
  const [countdown, setCountdown] = useState(0);
  const cancelPending = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } setCountdown(0); };
  useEffect(() => () => cancelPending(), []);  // eslint-disable-line
  const scheduleAdd = () => {
    cancelPending();
    let left = WAIT_SECS(); setCountdown(left);
    timerRef.current = setInterval(() => { left -= 1; if (left > 0) { setCountdown(left); return; } cancelPending(); const cur = dRef.current; if (cur.digits.every((x) => x !== null)) add(true, cur.qty || 1, cur.digits); }, 1000);
  };
  const setDigit = (v) => { if (active === null) return; const next = [...digits]; next[active] = v; patch({ digits: next }); const ne = next.findIndex((x) => x === null); setActive(ne === -1 ? null : ne); if (ne === -1) scheduleAdd(); };
  const reset = () => { cancelPending(); setDraft(null); setActive(null); };
  const add = (auto = false, q = qty, ds = digits) => {
    if (!ds.every((x) => x !== null)) { if (auto === true) return; toast.error(`Enter the ${n}-digit number`); setActive(ds.findIndex((x) => x === null)); return; }
    if (!q) { if (auto === true) return; toast.error("Enter quantity"); return; }
    const qty = q;
    const number = ds.join("");
    slats.forEach((sl) => onAdd({ game_id: game.id, game_name: game.name, slat_id: sl.id, slat_name: sl.name, number, quantity: qty, rate: rateOf(sl), rate_type: rateMode, mode: "direct", perms: 1 }));
    toast.success(`ALL BOARD · ${number} × ${qty} added to ${slats.map((sl) => boardOf(sl).positions.join("")).join(", ")}`);
    reset();
  };
  return (
    <div className="rl-card p-4 border-2 border-red-200" data-testid={`all-board-card-${group}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2"><span className="font-display font-bold text-slate-900 text-base">ALL BOARD</span><span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-red-100 text-red-700">{slats.map((sl) => boardOf(sl).positions.join("")).join(" + ")}</span></div>
          <div className="text-[11px] text-slate-500 mt-0.5">{tx("One number, one quantity — a ticket on every board")} · {slats.map((sl) => `${boardOf(sl).positions.join("")} ${fmt(rateOf(sl))}`).join(" · ")}</div>
        </div>
      </div>
      <div className="flex items-end gap-3 flex-wrap">
        {digits.map((x, i) => (
          <div key={i} className="flex flex-col items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-red-400 text-[11px] font-bold text-red-700">★</span>
            <button onClick={() => setActive(i)} className="rl-digit w-[52px] h-[52px] text-xl" style={{ border: "3px solid #ef4444", color: "#991b1b", ...(active === i ? { boxShadow: "0 0 0 3px #ef444444" } : {}) }} data-testid={`all-board-digit-${group}-${i}`}>{x === null ? "–" : x}</button>
          </div>
        ))}
        <button onClick={reset} disabled={!hasEntry} className="h-[52px] px-3 rounded-xl border-2 border-dashed border-red-300 text-red-600 text-xs font-bold flex items-center gap-1 disabled:opacity-30 active:scale-95" data-testid={`all-board-clear-${group}`}><Eraser className="w-4 h-4" />{tx("CLEAR")}</button>
      </div>
      {active !== null && (
        <div className="mt-3 grid grid-cols-5 gap-2 rl-fade-up" data-testid={`all-board-keypad-${group}`}>
          {Array.from({ length: 10 }, (_, v) => <button key={v} onClick={() => setDigit(v)} className="py-2.5 rounded-lg bg-slate-100 hover:bg-red-50 font-mono font-bold text-slate-900 text-base active:scale-95" data-testid={`all-board-key-${group}-${v}`}>{v}</button>)}
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap" data-testid={`all-board-summary-${group}`}>
        <div className="text-[11px] text-slate-600 leading-tight">
          <div><span className="text-slate-400">{tx("Number")}</span> <b className="font-mono">{digits.map((x) => (x === null ? "–" : x)).join("")}</b> · <span className="text-slate-400">{tx("Boards")}</span> <b>{slats.length}</b></div>
          <div><span className="text-slate-400">{tx("Qty")}</span> <b>{qty || 0}</b> × {slats.length} {tx("tickets")} = <b className="text-blue-700" data-testid={`all-board-amount-${group}`}>{fmt(total)}</b></div>
        </div>
        <div className="flex items-center gap-2">
          <QtyInput qty={qty} setQty={(q) => patch({ qty: q })} testId={`all-board-${group}`} onFocus={cancelPending} onCommit={(q) => add(true, q)} />
          <button onMouseDown={(e) => e.preventDefault()} onTouchStart={(e) => { e.preventDefault(); add(false); }} onClick={() => add(false)} className="bg-red-600 text-white px-5 py-2.5 rounded-full text-xs font-bold active:scale-95 transition-transform" data-testid={`all-board-add-${group}`}>ADD ALL</button>
        </div>
      </div>
      {countdown > 0 && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] font-bold text-red-900 bg-red-50 border border-red-300 rounded-full pl-3 pr-1 py-1 w-fit" data-testid={`all-board-countdown-${group}`}>
          <span className="w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center font-black" data-testid={`all-board-countdown-n-${group}`}>{countdown}</span>
          ALL BOARD · {qty || 1} SET add ஆகுது… Qty மாற்றலாம்
          <button type="button" onClick={cancelPending} className="ml-1 w-6 h-6 rounded-full bg-white border border-red-300 text-red-800 flex items-center justify-center" title="Wait" data-testid={`all-board-countdown-cancel-${group}`}>✕</button>
        </div>
      )}
    </div>
  );
}

export default function GameDetail() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const outletCfg = useOutletContext()?.config;
  const categoryOrder = outletCfg?.category_order;
  const { addItem, items: cartItems, removeItem } = useCart();
  const { user, loadUser: refreshUser } = useAuth();
  const { lang } = useI18n();
  const [game, setGame] = useState(null);
  const isFav = !!(user?.fav_shows || []).includes(game?.category);
  const toggleFav = async (e) => { e.preventDefault(); try { await api.put("/auth/favourite-shows", { category: game.category, on: !isFav }); toast.success(isFav ? "Unpinned" : `${game.category} pinned — Home strip & reminders focus on it`); refreshUser && refreshUser(); } catch { toast.error("Could not update"); } };
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  const [rateMode, setRateMode] = useState("customer");
  const [tab, setTab] = useState("results");
  const [results, setResults] = useState([]);
  const [orders, setOrders] = useState([]);
  // Per-board drafts survive category switches (and reloads) until ADD / CLEAR / booking
  const draftKey = `rl_draft_${gameId}`;
  const [drafts, setDrafts] = useState(() => { try { return JSON.parse(sessionStorage.getItem(draftKey) || "{}"); } catch { return {}; } });
  useEffect(() => { sessionStorage.setItem(draftKey, JSON.stringify(drafts)); }, [drafts, draftKey]);
  const setDraft = (id) => (v) => setDrafts((prev) => { const next = { ...prev }; if (v) next[id] = v; else delete next[id]; return next; });

  const load = useCallback(async (silent = false) => {
    if (silent !== true) setLoading(true);
    try {
      const { data } = await api.get(`/games/${gameId}`);
      setGame(data);
    } catch {
      toast.error("Game not found");
      navigate("/play");
    }
    setLoading(false);
  }, [gameId, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get(`/results?game_id=${gameId}&limit=10`).then((r) => setResults(r.data)).catch(() => {});
    const loadOrders = () => { if (user) api.get("/bets/my").then((r) => setOrders((r.data || []).filter((b) => b.game_id === gameId))).catch(() => {}); };
    loadOrders();
    window.addEventListener("rl-bets-placed", loadOrders);
    return () => window.removeEventListener("rl-bets-placed", loadOrders);
  }, [gameId, user]);

  if (loading) return <div className="space-y-3"><Skeleton className="h-32 w-full rounded-2xl" />{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)}</div>;
  if (!game) return null;

  const rt = game.runtime || {};
  const groups = orderedGroups(categoryOrder).filter((g) => game.slats.some((s) => boardOf(s).group === g.id));
  const activeGroup = groups.some((g) => g.id === group) ? group : (groups[0]?.id || "single");
  const shown = game.slats.filter((s) => boardOf(s).group === activeGroup);
  const isPartyUser = user?.account_type === "party";
  const activeRateMode = isPartyUser ? rateMode : "customer";

  return (
    <div className="space-y-3 pb-4">
      <RateNoticeBanner notice={outletCfg?.rate_notice} />
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-slate-500 text-sm font-medium" data-testid="game-back-btn"><ArrowLeft className="w-4 h-4" />{tx("Back")}</button>
      {user && <BookedStrip games={[game]} bets={orders.filter((b) => rt.mode !== "interval" || b.draw_time === rt.draw_slot || b.draw_time >= (rt.draw_slot || ""))} testId="game-booked-strip" />}

      {rt.mode === "interval" && user && <StreakChip gameId={game.id} lang={lang} />}
      {rt.mode === "interval" && rt.pending_slot && rt.draw_slot && (
        <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 px-3.5 py-2.5 flex items-start gap-2 rl-pop" data-testid="slot-switch-banner">
          <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900">
            <div className="font-black text-sm">⏱ {to12h(rt.pending_slot)} booking CLOSED · {lang === "ta" ? "இப்போது book பண்ணும் ticket" : "tickets you book now are for the"} <span className="bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded-md">{to12h(rt.draw_slot)}</span> {lang === "ta" ? "draw-க்கு" : "draw"}</div>
            <div className="text-[11px] mt-0.5">{to12h(rt.pending_slot)} result in <Countdown seconds={rt.pending_draw_seconds} className="font-bold text-amber-700" /> · {lang === "ta" ? `${to12h(rt.draw_slot)} booking closes` : `${to12h(rt.draw_slot)} booking closes in`} <Countdown seconds={rt.closing_seconds} className="font-bold text-amber-700" /></div>
          </div>
        </div>
      )}
      {/* Draw info card */}
      <RoyalStage game={game} image={(outletCfg?.category_images || {})[game.category] || game.logo} drawTime={rt.mode === "interval" ? (rt.draw_slot || game.draw_time) : game.draw_time} className="rounded-2xl p-4 shadow-lg" testId="draw-info-card">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display font-black text-white text-xl flex items-center gap-2 flex-wrap">{game.name}<WinBoostBadge ta={lang === "ta"} /></h1>
            <span className="text-[10px] uppercase tracking-widest text-blue-100 flex items-center gap-1.5">{game.category}
              <button onClick={toggleFav} className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-black border ${isFav ? "bg-amber-400 text-slate-900 border-amber-300" : "bg-white/15 text-white border-white/30"}`} data-testid="fav-show-btn" title="Pin this show category"><Star className={`w-3 h-3 ${isFav ? "fill-slate-900" : ""}`} />{isFav ? "PINNED" : "PIN"}</button>
            </span>
          </div>
          {game.last_result && (
            <div className="text-right">
              <div className="text-[9px] text-blue-100 flex items-center gap-1 justify-end mb-1"><Trophy className="w-3 h-3" />{tx("Last Result")}</div>
              <ResultBalls number={game.last_result.winning_number} size="w-7 h-7 text-xs" />
              {rt.mode !== "interval" && <div className="mt-1 flex justify-end"><StoryButton resultId={game.last_result.id} compact /></div>}
            </div>
          )}
        </div>
        {game.fair_locked && <QueenReveal game={game} ta={lang === "ta"} />}
        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          <div className="bg-white/15 rounded-xl py-2"><div className="text-[9px] text-blue-100">{tx("CLOSE")}</div><div className="text-sm font-bold">{rt.mode === "interval" ? (rt.close_slot || "—") : game.close_time}</div></div>
          <div className="bg-white/15 rounded-xl py-2"><div className="text-[9px] text-blue-100">{rt.mode === "interval" ? "NEXT DRAW" : "RESULT"}</div><div className="text-sm font-bold" data-testid="draw-slot">{rt.mode === "interval" ? (rt.draw_slot || "—") : game.draw_time}</div></div>
          <div className="bg-white/15 rounded-xl py-2"><div className="text-[9px] text-blue-100 flex items-center gap-0.5 justify-center"><Clock className="w-2.5 h-2.5" />{tx("CLOSES IN")}</div><div className="text-sm font-bold"><Countdown seconds={rt.closing_seconds} className="text-amber-300" onExpire={() => load(rt.mode === "interval")} /></div></div>
        </div>
        {rt.mode === "interval" && (
          <div className="mt-3 text-[11px] text-blue-100 bg-white/10 rounded-xl px-3 py-2" data-testid="interval-info">
            ⚡ Draw every {rt.interval_minutes} min from {rt.first_draw_time || game.open_time} to {rt.last_draw_time} (booking opens {game.open_time}). Booking closes {game.close_before_minutes || 5} min before each draw.
          </div>
        )}
        <div className="mt-2 text-[9px] tracking-widest text-white/60 text-center" data-testid="draw-vibe-label">✦ {slotLabel(rt.mode === "interval" ? (rt.draw_slot || game.draw_time) : game.draw_time, lang)} ✦</div>
      </RoyalStage>

      {rt.mode === "interval" && <LuckyLive game={game} onSettled={() => load(true)} />}
      {rt.mode !== "interval" && game.live_reveal && <LuckyLive game={game} onSettled={() => load(false)} />}
      {rt.mode === "interval" && <LuckyHotNumbers results={results} game={game} canBet={rt.is_open && !!user} />}
      {user && <RepeatLastRound game={game} disabled={!rt.is_open} />}

      {!rt.is_open && (
        <div className="rl-card p-3 text-center text-red-500 text-sm font-semibold border-red-200" data-testid="game-closed-notice">
          {rt.mode === "interval" ? `Today's rounds are over. Next round opens tomorrow at ${game.open_time}.` : "Betting is closed for this draw. Check other games."}
        </div>
      )}

      <div className="flex items-center justify-between mt-2">
        <h2 className="font-display font-bold text-slate-900">{tx("Select Board & Number")}</h2>
        <RateCardSheet slats={game.slats} rateMode={activeRateMode} isPartyUser={isPartyUser} />
      </div>
      {isPartyUser && (
        <div className="flex gap-2 rl-card p-1" data-testid="rate-mode-toggle">
          {[["customer", "Customer Rate"], ["party", "Party Rate"]].map(([m, l]) => (
            <button key={m} onClick={() => setRateMode(m)} data-testid={`rate-mode-${m}`}
              className={`flex-1 py-2 rounded-lg text-xs font-bold ${rateMode === m ? (m === "party" ? "bg-amber-500 text-white" : "rl-gold-bg") : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      )}
      {groups.length === 0 ? (
        <div className="rl-card p-4 text-center text-slate-500 text-sm">{tx("No bet boards configured for this game yet.")}</div>
      ) : (
        <>
          <WinPatternBoard gameId={game.id} onPick={(pos, digit) => { api.post("/guess-log", { game_id: game.id, board: pos, number: String(digit), source: "pattern" }).catch(() => {}); const sl = game.slats.find((s) => boardOf(s).positions.join("") === pos); if (!sl) return; setGroup("single"); setDrafts((prev) => ({ ...prev, [sl.id]: { digits: [digit], qty: prev[sl.id]?.qty || 1, mode: "direct" } })); toast.success(`${pos} = ${digit} filled`); }} />
          <div className="flex gap-2 flex-wrap" data-testid="bet-group-tabs">
            {groups.map((g) => (
              <button key={g.id} onClick={() => setGroup(g.id)} data-testid={`bet-group-${g.id}`}
                className={`flex-1 min-w-[70px] py-2.5 rounded-xl text-sm font-bold ${activeGroup === g.id ? "rl-gold-bg" : "rl-card text-slate-600"}`}>
                {g.label}
              </button>
            ))}
          </div>
          {rt.is_open ? (
            <div className="space-y-3">
              {activeGroup === "double" && (() => {
                const singles = game.slats.filter((s) => boardOf(s).group === "single" && boardOf(s).tag !== "ALL BOARD");
                const digitOf = (pos) => { const sl = singles.find((s) => boardOf(s).positions.join("") === pos); const dr = sl && drafts[sl.id]; return dr && dr.digits?.[0] != null ? { d: dr.digits[0], q: dr.qty || 1 } : null; };
                const A = digitOf("A"), B = digitOf("B"), C = digitOf("C");
                const filled = [A, B, C].filter(Boolean).length;
                if (filled < 2) return null;
                const copy = () => {
                  const next = { ...drafts }; let n = 0;
                  shown.filter((s) => boardOf(s).tag !== "ALL BOARD").forEach((s) => {
                    const pos = boardOf(s).positions; const src = { A, B, C };
                    if (pos.length === 2 && src[pos[0]] && src[pos[1]]) { next[s.id] = { digits: [src[pos[0]].d, src[pos[1]].d], qty: src[pos[0]].q, mode: "direct" }; n += 1; }
                  });
                  setDrafts(next); toast.success(`${n} double boards filled from Singles (A${A ? A.d : "–"} B${B ? B.d : "–"} C${C ? C.d : "–"})`);
                };
                return <button onClick={copy} className="w-full rl-card border-dashed border-blue-300 py-2.5 rounded-2xl text-xs font-bold text-blue-800 flex items-center justify-center gap-1" data-testid="copy-singles-to-doubles">⇢ {tx("Copy Singles → Doubles")} · A{A ? A.d : "–"} B{B ? B.d : "–"} C{C ? C.d : "–"} → AB · BC · AC</button>;
              })()}
              {(activeGroup === "single" || activeGroup === "double") && shown.filter((s) => boardOf(s).tag !== "ALL BOARD").length > 1 && (
                <AllBoardCard group={activeGroup} slats={shown.filter((s) => boardOf(s).tag !== "ALL BOARD")} game={game} onAdd={addItem} rateMode={activeRateMode} draft={drafts[`all_${activeGroup}`]} setDraft={setDraft(`all_${activeGroup}`)} />
              )}
              {shown.map((s) => <SlatCard key={s.id} slat={s} game={game} onAdd={addItem} rateMode={activeRateMode} results={results} draft={drafts[s.id]} setDraft={setDraft(s.id)} cartItems={cartItems} onRemove={removeItem} />)}
            </div>
          ) : (
            <div className="space-y-3 opacity-50 pointer-events-none">
              {shown.slice(0, 3).map((s) => <SlatCard key={s.id} slat={s} game={game} onAdd={() => {}} draft={null} setDraft={() => {}} />)}
            </div>
          )}
        </>
      )}

      {/* Result history / My Orders */}
      <div className="rl-card p-4 mt-4">
        <div className="flex gap-6 border-b border-slate-100 mb-3">
          <button onClick={() => setTab("results")} data-testid="tab-results" className={`pb-2 text-sm font-bold ${tab === "results" ? "text-blue-600 border-b-2 border-blue-600" : "text-slate-400"}`}>{tx("Result History")}</button>
          <button onClick={() => setTab("orders")} data-testid="tab-orders" className={`pb-2 text-sm font-bold ${tab === "orders" ? "text-blue-600 border-b-2 border-blue-600" : "text-slate-400"}`}>{tx("My Orders")}</button>
        </div>
        {tab === "results" ? (
          results.length === 0 ? <p className="text-center text-xs text-slate-400 py-6" data-testid="results-empty">{tx("No results yet")}</p> : (
            <div className="divide-y divide-slate-100" data-testid="results-list">
              {results.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <div className="text-xs font-mono text-slate-700 flex items-center gap-1">{r.issue_id || r.draw_date}{(r.locked || game.fair_locked) && <span className="inline-flex items-center gap-0.5 text-[8px] font-black text-violet-700 bg-violet-100 rounded-full px-1.5 py-0.5" data-testid={`result-locked-${r.id}`}><Lock className="w-2.5 h-2.5" />LOCKED</span>}</div>
                    <div className="text-[10px] text-slate-400">{r.draw_date} {r.draw_time || ""}</div>
                  </div>
                  <ResultBalls number={r.winning_number} size="w-7 h-7 text-xs" />
                </div>
              ))}
            </div>
          )
        ) : (
          !user ? <p className="text-center text-xs text-slate-400 py-6" data-testid="orders-login">{tx("Login to see your orders")}</p> :
          orders.length === 0 ? <p className="text-center text-xs text-slate-400 py-6" data-testid="orders-empty">{tx("No orders for this game yet")}</p> : (
            <div className="divide-y divide-slate-100" data-testid="orders-list">
              {orders.map((b) => (
                <div key={b.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <div className="flex items-center gap-2"><span className="rl-ball w-7 h-7 text-xs">{b.number}</span><span className="text-xs text-slate-600">{b.slat_name}</span>{b.bet_mode === "box" && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">BOX ×{b.box_size}{b.box_hit ? ` · hit ${b.box_hit}` : ""}</span>}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5"><span className="font-bold text-slate-600" data-testid={`order-rate-${b.id}`}>{rateLine(b)}</span> · {dateTimeLabel(b.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-bold text-slate-900">{fmt(b.total_amount || b.amount)}</div>
                    <div className={`text-[10px] font-bold uppercase ${b.status === "won" ? "text-emerald-500" : b.status === "lost" ? "text-red-500" : "text-blue-500"}`}>{b.status}</div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
